import type {
  DurationRange,
  MarketplaceDeliverableType,
  MarketplaceProfilePackage,
  MarketplaceProfilePackageDeliverable,
} from '@foodfluence/shared';
import { deliverableIsCarousel } from '@foodfluence/shared';
import { lineLabel, type PlatformId, type TypeId } from './packageContentTypes';

const UGC_DURATION_PRESETS: Record<
  Exclude<TypeId, 'reel' | 'post' | 'story' | 'carousel' | 'video' | 'photoCarousel' | 'ugcPhoto' | 'ugcPhotoCarousel'>,
  DurationRange
> = {
  ugcShort: { min: 0, max: 30, unit: 'seconds', displayText: '0–30s' },
  ugcMedium: { min: 30, max: 60, unit: 'seconds', displayText: '30–60s' },
  ugcLong: { min: 60, unit: 'seconds', displayText: '60s+' },
  ugcStory: { min: 0, max: 15, unit: 'seconds', displayText: '15s' },
};

function lineTypeToDeliverableType(platform: PlatformId, type: TypeId): MarketplaceDeliverableType {
  if (platform === 'instagram') {
    if (type === 'reel') return 'instagram_reel';
    if (type === 'post') return 'instagram_post';
    if (type === 'story') return 'instagram_story';
    if (type === 'carousel') return 'instagram_carousel';
  }
  if (platform === 'tiktok') {
    if (type === 'video') return 'tiktok';
    if (type === 'photoCarousel') return 'tiktok_photo_carousel';
  }
  if (platform === 'ugc') {
    if (type === 'ugcPhoto') return 'ugc_photo';
    if (type === 'ugcPhotoCarousel') return 'ugc_photo_carousel';
    if (type === 'ugcShort' || type === 'ugcMedium' || type === 'ugcLong' || type === 'ugcStory')
      return 'ugc_video';
  }
  throw new Error(`No deliverable mapping for ${platform} / ${type}`);
}

function ugcTypeToDuration(type: TypeId): DurationRange | undefined {
  if (type === 'ugcShort' || type === 'ugcMedium' || type === 'ugcLong' || type === 'ugcStory') {
    return UGC_DURATION_PRESETS[type as keyof typeof UGC_DURATION_PRESETS];
  }
  return undefined;
}

export function lineToDeliverable(line: {
  platform: PlatformId;
  type: TypeId;
  quantity: number;
  count?: number;
}): MarketplaceProfilePackageDeliverable {
  const type = lineTypeToDeliverableType(line.platform, line.type);
  const isCarousel = deliverableIsCarousel(type);
  const duration = line.platform === 'ugc' && line.type !== 'ugcPhoto' && line.type !== 'ugcPhotoCarousel'
    ? ugcTypeToDuration(line.type)
    : undefined;
  return {
    type,
    quantity: isCarousel ? 1 : line.quantity,
    ...(isCarousel && line.count ? { count: line.count } : {}),
    ...(duration ? { duration } : {}),
  };
}

export function linesToDeliverables(
  lines: PackageContentLine[]
): MarketplaceProfilePackageDeliverable[] {
  return lines.map(lineToDeliverable);
}

function durationToUgcTypeId(duration: DurationRange | undefined): TypeId {
  if (!duration) return 'ugcShort';
  const min = duration.min ?? 0;
  const max = duration.max;
  if (typeof max === 'number' && max <= 15) return 'ugcStory';
  if (typeof max === 'number' && max <= 30) return 'ugcShort';
  if (min >= 30 && typeof max === 'number' && max <= 60) return 'ugcMedium';
  if (min >= 60 || max === undefined) return 'ugcLong';
  return max <= 60 ? 'ugcMedium' : 'ugcLong';
}

export function deliverableToPlatformType(
  d: MarketplaceProfilePackageDeliverable
): { platform: PlatformId; type: TypeId } {
  const t = d.type as string;
  if (t === 'instagram_reel' || t === 'instagramReels') return { platform: 'instagram', type: 'reel' };
  if (t === 'instagram_post' || t === 'instagramPosts') return { platform: 'instagram', type: 'post' };
  if (t === 'instagram_story' || t === 'instagramStories') return { platform: 'instagram', type: 'story' };
  if (t === 'instagram_carousel') return { platform: 'instagram', type: 'carousel' };
  if (t === 'tiktok' || t === 'video') return { platform: 'tiktok', type: 'video' };
  if (t === 'tiktok_photo_carousel') return { platform: 'tiktok', type: 'photoCarousel' };
  if (t === 'ugc_photo' || t === 'ugcPhoto' || t === 'photo') return { platform: 'ugc', type: 'ugcPhoto' };
  if (t === 'ugc_photo_carousel') return { platform: 'ugc', type: 'ugcPhotoCarousel' };
  if (t === 'ugc_video') return { platform: 'ugc', type: durationToUgcTypeId(d.duration) };
  // legacy values
  if (t === 'ugcShortForm') return { platform: 'ugc', type: 'ugcShort' };
  if (t === 'ugcMediumForm') return { platform: 'ugc', type: 'ugcMedium' };
  if (t === 'ugcLongForm') return { platform: 'ugc', type: 'ugcLong' };
  if (t === 'ugcStory') return { platform: 'ugc', type: 'ugcStory' };
  throw new Error(`Unknown deliverable type: ${t}`);
}

const newContentLineId = () => `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export type PackageContentLine = {
  id: string;
  platform: PlatformId;
  type: TypeId;
  quantity: number;
  /** Slide/photo count for carousel types. */
  count?: number;
};

export function deliverablesToContentLines(
  deliverables: MarketplaceProfilePackageDeliverable[]
): PackageContentLine[] {
  if (deliverables.length === 0) return [];
  return deliverables.map((d) => {
    const { platform, type } = deliverableToPlatformType(d);
    return {
      id: newContentLineId(),
      platform,
      type,
      quantity: d.quantity,
      ...(d.count !== undefined ? { count: d.count } : {}),
    };
  });
}

export function labelForDeliverable(d: MarketplaceProfilePackageDeliverable): string {
  const { type } = deliverableToPlatformType(d);
  return lineLabel(type);
}

export function localPackageToApiRow(row: {
  id: string;
  price: string;
  description: string;
  featured?: boolean;
  deliverables: MarketplaceProfilePackageDeliverable[];
}): MarketplaceProfilePackage {
  return {
    id: row.id,
    price: row.price,
    description: row.description.trim() || undefined,
    deliverables: row.deliverables,
    featured: row.featured,
  };
}

export function apiPackageToLocalRow(pkg: MarketplaceProfilePackage): {
  id: string;
  price: string;
  description: string;
  featured?: boolean;
  deliverables: MarketplaceProfilePackageDeliverable[];
} {
  return {
    id: pkg.id,
    price: pkg.price,
    description: pkg.description ?? '',
    featured: pkg.featured,
    deliverables: pkg.deliverables ?? [],
  };
}
